// Phase 24 M5 — devices list.

import { useEffect, useState } from 'react';
import axios from 'axios';
import type { Device } from '@leyrochain/shared';

import { listDevices, revokeDevice } from '../../api/profile';

function describeAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { reason?: string; error?: string } | undefined;
    return data?.reason ?? data?.error ?? err.message ?? fallback;
  }
  return fallback;
}

export function DevicesList(): JSX.Element {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    try {
      const res = await listDevices();
      setDevices(res.devices);
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido cargar a lista.'));
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onRevoke(id: string): Promise<void> {
    setError(null);
    setRevoking(id);
    try {
      await revokeDevice(id);
      await refresh();
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido pechar a sesión.'));
    } finally {
      setRevoking(null);
    }
  }

  const surface: React.CSSProperties = {
    background: '#FFFFFF',
    border: '1px solid #DDDDDD',
    color: '#222222',
    borderRadius: 'var(--radius-md)',
  };

  return (
    <section className="p-5" style={surface}>
      <h3 className="text-lg font-semibold mb-3">Dispositivos abertos</h3>
      {error ? (
        <p role="alert" className="text-sm mb-3" style={{ color: '#B23A3A' }}>
          {error}
        </p>
      ) : null}
      {devices === null ? (
        <p style={{ color: '#717171' }}>Cargando…</p>
      ) : devices.length === 0 ? (
        <p style={{ color: '#717171' }}>Aínda non hai dispositivos rexistrados.</p>
      ) : (
        <ul className="space-y-2">
          {devices.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between p-3 rounded-md"
              style={{ background: '#F7F7F7' }}
            >
              <div>
                <div className="text-sm font-medium">
                  {d.device_name ?? 'Dispositivo'}{' '}
                  <span
                    className="text-xs ml-1 px-1.5 py-0.5 rounded"
                    style={{
                      background: '#FFFFFF',
                      border: '1px solid #DDDDDD',
                    }}
                  >
                    {d.platform}
                  </span>
                </div>
                <div className="text-xs" style={{ color: '#717171' }}>
                  Última actividade: {new Date(d.last_active_at).toLocaleString('gl-ES')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onRevoke(d.id)}
                disabled={revoking === d.id}
                className="text-sm px-3 py-1.5 rounded-md disabled:opacity-60"
                style={{
                  background: '#FFFFFF',
                  color: '#B23A3A',
                  border: '1px solid #B23A3A',
                }}
              >
                {revoking === d.id ? 'Pechando…' : 'Pechar sesión'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
